import { rem } from 'polished'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import BookedItemExtendDatesModal from './BookedItemExtendDatesModal'
import ConfirmNavigateToOfferModal from './ConfirmNavigateToOfferModal'
import ConfirmUpdateTripDatesModal from './ConfirmUpdateTripDatesModal'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineAngleRightBIcon from 'LuxKit/Icons/line/LineAngleRightBIcon'
import LineCalendarIcon from 'LuxKit/Icons/line/LineCalendarIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import BodyText from 'LuxKit/Typography/BodyText'
import { DMY_CASUAL_SHORT_FORMAT } from 'constants/dateFormats'
import TripContext, { useTripId } from 'tripPlanner/contexts/TripContext'
import { useUpdateTrip } from 'tripPlanner/hooks/api/trip'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { ItemMetadata } from 'tripPlanner/models'
import type { TripItem } from 'tripPlanner/types/tripItem'
import { formatCasualDateRange, isLeBooking, isLeItem } from 'tripPlanner/utils'
import DateChangeView from './DateChangeView'

const StyledModalContent = styled(ModalContent)`
  padding-bottom: ${rem(20)};
`

const TextWrapper = styled.div`
  margin: ${rem(0)} ${rem(16)} ${rem(8)} ${rem(16)};
`

// This modal would not open if the trip did not have dates.
type TripContextWithDates = {
  startDate: moment.Moment
  endDate: moment.Moment
}

type TripItemWithDates = TripItem & {
  startDate: moment.Moment
  endDate?: moment.Moment
}

interface Props {
  item: TripItemWithDates
  changeItemDates: () => void
}

function UpdateItemOrTripDatesModal({ item, changeItemDates }: Props) {
  const tripContext = useContext(TripContext) as TripContextWithDates
  const tripId = useTripId()

  const intl = useIntl()
  const openModal = useOpenModal()
  const { closeModal } = useModalCallbacks()

  const { mutate, isLoading } = useUpdateTrip({
    onSuccess: () => {
      closeModal()
      showSnackbar(intl.formatMessage({
        id: 'LM_x8-BFRdAf6wOqPGxv',
        defaultMessage: 'Dates saved',
        description: 'Success notification displayed after saving updated trip dates',
      }), 'success')
    },
    onError: () => {
      showSnackbar(
        'Error while updating trip dates. Please try again.',
        'critical',
      )
    },
  })

  const finalDates = useMemo(() => {
    let finalStartDate = tripContext.startDate
    let finalEndDate = tripContext.endDate
    const itemStartDate = item.startDate

    if (finalStartDate > itemStartDate) {
      finalStartDate = itemStartDate
    }

    if ('endDate' in item && item.endDate && finalEndDate < item.endDate) {
      finalEndDate = item.endDate
    } else if (itemStartDate > finalEndDate) {
      finalEndDate = itemStartDate
    }

    return { finalStartDate, finalEndDate }
  }, [item, tripContext.endDate, tripContext.startDate])

  const updateTripDates = useCallback(() => {
    const { finalStartDate, finalEndDate } = finalDates

    mutate({
      tripId,
      startDate: finalStartDate,
      endDate: finalEndDate,
    })
  }, [finalDates, mutate, tripId])

  const Icon = ItemMetadata[item.type].icon

  const updateItem = () => {
    if (isLeItem(item)) {
      openModal(ConfirmNavigateToOfferModal, { changeItemDates, item })
    } else {
      changeItemDates()
    }
  }

  const confirmUpdateTripDates = () => {
    openModal(ConfirmUpdateTripDatesModal, {
      updateTripDates,
      currentTripDates: formatCasualDateRange(
        tripContext.startDate,
        tripContext.endDate,
      ),
      extendedTripDates: formatCasualDateRange(
        finalDates.finalStartDate,
        finalDates.finalEndDate,
      ),
      isLoading,
    })
  }

  if (isLeBooking(item)) {
    return (
      <BookedItemExtendDatesModal
        item={item}
        updateTripDates={updateTripDates}
        currentTripDates={formatCasualDateRange(
          tripContext.startDate,
          tripContext.endDate,
        )}
        extendedTripDates={formatCasualDateRange(
          finalDates.finalStartDate,
          finalDates.finalEndDate,
        )}
        isLoading={isLoading}
      />
    )
  }

  const tripDates = formatCasualDateRange(
    tripContext.startDate,
    tripContext.endDate,
  )
  const itemDates = 'endDate' in item && item.endDate ?
      formatCasualDateRange(item.startDate, item.endDate) :
      item.startDate.format(DMY_CASUAL_SHORT_FORMAT)

  return (
    <>
      <ModalHeader
        title={intl.formatMessage({
          id: '4jPlZsDWPUfUbGmj0I_x',
          defaultMessage: 'Oops! Date mismatch',
          description: 'Modal title when item dates are outside the trip date range',
        })}
        onCloseButtonClick={closeModal}
      />
      <ModalBody>
        <StyledModalContent>
          <VerticalSpacer gap={24}>
            <BodyText variant="large" colour="neutral-two">
              {intl.formatMessage(
                {
                  id: 'vvX87SwmM0-t8Disjl2l',
                  defaultMessage: 'The chosen {itemType} dates sit outside your overall trip dates',
                  description: 'Explanation that the item dates do not fall within the trip date range',
                },
                { itemType: intl.formatMessage(ItemMetadata[item.type].singularName).toLowerCase() },
              )}
            </BodyText>
            <DateChangeView
              tripDates={tripDates}
              itemDates={itemDates}
              itemType={intl.formatMessage(ItemMetadata[item.type].singularName)}
            />
            <div>
              <TextWrapper>
                <b>{intl.formatMessage({
                  id: 'TZsLvsBr-SGHXrNXyTDF',
                  defaultMessage: 'What would you like to do?',
                  description: 'Prompt asking user how to resolve a date mismatch between item and trip',
                })}
                </b>
              </TextWrapper>
              {!isLeBooking(item) && (
                <ClickableListItem
                  startIcon={<Icon />}
                  title={intl.formatMessage(
                    {
                      id: 'OMHAXzIILUlkewv9RJQr',
                      defaultMessage: 'Adjust {itemType} dates',
                      description: 'Option to change the dates of a trip item in the trip planner',
                    },
                    { itemType: intl.formatMessage(ItemMetadata[item.type].singularName).toLowerCase() },
                  )}
                  endIcon={<LineAngleRightBIcon />}
                  onClick={updateItem}
                />
              )}

              <ClickableListItem
                startIcon={<LineCalendarIcon />}
                title={intl.formatMessage({
                  id: 'r93qNd3Ui-GoWSkrSWvW',
                  defaultMessage: 'Extend trip duration',
                  description: 'Option to extend the trip dates to include the item dates',
                })}
                endIcon={<LineAngleRightBIcon />}
                onClick={confirmUpdateTripDates}
              />
            </div>
          </VerticalSpacer>
        </StyledModalContent>
      </ModalBody>
    </>
  )
}

export default UpdateItemOrTripDatesModal
